/* eslint-disable react-refresh/only-export-components -- arquivo mistura componente com hook/types; mesma estrutura que CampaignContext. */
import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from 'react'
import { useClients } from '@/features/clients/hooks/use-clients'
import { useAuth } from '@/contexts/AuthContext'
import type { Client } from '@/types'

const STORAGE_KEY = 'runna_active_client_id'
/** Valor especial em localStorage indicando "Todos os clientes". null/undefined = decidir auto. */
const ALL_CLIENTS = 'all'

interface ClientContextType {
  /** Lista de clientes visíveis ao usuário (filtrada por RLS no banco). */
  clients: Client[]
  /** Cliente ativo: null = "Todos os clientes" (vê tudo do tenant). */
  activeClient: Client | null
  /** ID do cliente ativo, null = "Todos". */
  activeClientId: string | null
  /** Troca o cliente ativo. Passar null = "Todos os clientes". */
  setActiveClientId: (id: string | null) => void
  /** Para client_users: o seletor é fixo no único client_id deles, não troca. */
  isLocked: boolean
  loading: boolean
}

const ClientContext = createContext<ClientContextType | undefined>(undefined)

export function ClientProvider({ children }: { children: ReactNode }) {
  const { profile } = useAuth()
  const { clients, loading } = useClients()

  // Para client_users, o cliente ativo é fixo (vem do client_profile).
  // Para team_members, lê de localStorage (com default null = "Todos").
  const isClientUser = profile?.user.user_type === 'client_user'
  const lockedClientId = isClientUser ? (profile?.client?.client_id ?? null) : null

  const [savedId, setSavedIdState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw || raw === ALL_CLIENTS) return null
    return raw
  })

  // client_user: se já há lockedClientId, sobrescrever savedId
  useEffect(() => {
    if (isClientUser && lockedClientId && savedId !== lockedClientId) {
      // Sincronização legítima: profile carregou async, agora sabemos o client_id.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSavedIdState(lockedClientId)
    }
  }, [isClientUser, lockedClientId, savedId])

  const activeClientId = isClientUser ? lockedClientId : savedId

  const activeClient = useMemo(
    () => (activeClientId ? clients.find(c => c.id === activeClientId) ?? null : null),
    [activeClientId, clients],
  )

  // Auto-correct: cliente ativo não está mais visível (foi removido / tenant trocou)
  useEffect(() => {
    if (isClientUser) return // client_user não pode mexer
    if (savedId && clients.length > 0 && !clients.some(c => c.id === savedId)) {
      // Sincronização legítima com fonte externa (lista de clients carregada
      // async): id persistido aponta pra client que sumiu → normaliza pra ALL.
      // Mesmo padrão do effect de client_user acima.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSavedIdState(null)
      localStorage.setItem(STORAGE_KEY, ALL_CLIENTS)
    }
  }, [savedId, clients, isClientUser])

  const setActiveClientId = (id: string | null) => {
    if (isClientUser) return // bloqueado
    setSavedIdState(id)
    localStorage.setItem(STORAGE_KEY, id ?? ALL_CLIENTS)
  }

  return (
    <ClientContext.Provider value={{
      clients, activeClient, activeClientId,
      setActiveClientId, isLocked: isClientUser, loading,
    }}>
      {children}
    </ClientContext.Provider>
  )
}

export function useClientContext() {
  const ctx = useContext(ClientContext)
  if (!ctx) throw new Error('useClientContext must be used within ClientProvider')
  return ctx
}
